import { useEffect } from 'react';
import {
  type FieldPath,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodSchema } from 'zod';
import type { UseMutationResult } from '@tanstack/react-query';
import { useToast } from '../stores/toast.store';
import { describeApiError, extractApiError, fieldErrorsFromApiError } from './errors';

/**
 * Hook genérico para os FormDialogs CRUD. Encapsula o boilerplate comum:
 *
 * - `useForm` com `zodResolver`.
 * - Reset dos valores e foco programático quando o diálogo abre.
 * - `onSubmit` que chama create/update e dispara toast.
 * - Mapeamento de `camposComErro` para `form.setError` dos campos,
 *   `setError('root', describeApiError)` para erro geral exibido pelo `FormDialog`.
 *
 * Convenção: passar `toInput?` quando o payload da API diferir do form (ex.:
 * vazio → null). Default: o próprio `values` vai como input.
 */
export interface UseCrudFormDialogOptions<TFormValues extends FieldValues, TEntity, TInput, TId> {
  open: boolean;
  /** `null` = modo Create; objeto = modo Edit. */
  entity: TEntity | null;
  schema: ZodSchema<TFormValues>;
  defaultValues: TFormValues;
  /** Projeta o entity para os valores do formulário (modo Edit). */
  fromEntity: (entity: TEntity) => TFormValues;
  /** Transforma valores do form no payload da API. Default: identidade. */
  toInput?: (values: TFormValues) => TInput;
  /** Extrai o ID do entity (usado em update). */
  getId: (entity: TEntity) => TId;
  hooks: {
    useCreate: () => UseMutationResult<TEntity, Error, TInput>;
    useUpdate: () => UseMutationResult<TEntity, Error, { id: TId; input: TInput }>;
  };
  /** Mensagens de toast i18n-resolvidas pela feature. */
  messages: { created: string; updated: string };
  /** Campo a focar ao abrir. */
  focusField?: FieldPath<TFormValues>;
  onClose: () => void;
}

export interface CrudFormDialogState<TFormValues extends FieldValues> {
  form: UseFormReturn<TFormValues>;
  isEdit: boolean;
  onSubmit: SubmitHandler<TFormValues>;
  serverError: string | null;
}

export function useCrudFormDialog<TFormValues extends FieldValues, TEntity, TInput, TId>(
  options: UseCrudFormDialogOptions<TFormValues, TEntity, TInput, TId>,
): CrudFormDialogState<TFormValues> {
  const {
    open,
    entity,
    schema,
    defaultValues,
    fromEntity,
    toInput,
    getId,
    hooks,
    messages,
    focusField,
    onClose,
  } = options;
  const toast = useToast();
  const isEdit = entity !== null;

  const create = hooks.useCreate();
  const update = hooks.useUpdate();

  const form = useForm<TFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as never, // type-cast: RHF Resolver vs DefaultValues
  });

  useEffect(() => {
    if (!open) return;
    form.reset(entity ? fromEntity(entity) : defaultValues);
    if (focusField) {
      const handle = setTimeout(() => form.setFocus(focusField), 0);
      return () => clearTimeout(handle);
    }
    return undefined;
    // entity?.id mudaria a re-popular, mas o tipo de entity varia; reiniciamos
    // quando `open` flippa para true. Atualizações in-flight são responsabilidade
    // do consumer (raro neste padrão CRUD).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity]);

  const onSubmit: SubmitHandler<TFormValues> = async (values) => {
    const payload = toInput ? toInput(values) : (values as unknown as TInput);
    try {
      if (isEdit && entity) {
        await update.mutateAsync({ id: getId(entity), input: payload });
        toast.success(messages.updated);
      } else {
        await create.mutateAsync(payload);
        toast.success(messages.created);
      }
      onClose();
    } catch (err) {
      const apiError = extractApiError(err);
      if (apiError) {
        for (const fe of fieldErrorsFromApiError(apiError)) {
          // Resolves M3 do review T-12: warn em DEV quando o backend manda um
          // campo que não casa com nenhum input do form (mapeamento silencioso
          // mascarava bugs de contrato).
          if (fe.campo in defaultValues) {
            form.setError(fe.campo as FieldPath<TFormValues>, { message: fe.mensagem });
          } else if (import.meta.env.DEV) {
            console.warn(
              `[useCrudFormDialog] Campo "${fe.campo}" veio no camposComErro mas não existe no formulário. Verifique o contrato da API ou os defaultValues.`,
              { campo: fe.campo, mensagem: fe.mensagem },
            );
          }
        }
      }
      form.setError('root' as FieldPath<TFormValues>, { message: describeApiError(err) });
    }
  };

  // RHF tipa errors.root como FieldError dinâmico; coerção mínima.
  const rootError = (form.formState.errors as Record<string, { message?: string } | undefined>)
    .root;
  const serverError = rootError?.message ?? null;

  return { form, isEdit, onSubmit, serverError };
}
